import {
  useInfiniteQueryUserMemberSessionPackSubscriptionCurrent,
  useInfiniteQueryUserMemberSessionPackSubscriptionPrevious,
  useInfiniteQueryUserMemberSessionPackSubscriptionUsageHistory,
} from "@gymflow/api";
import useGymflowModels from "apps/portal/src/store";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, SearchIcon, ShopBagIcon, Spinner, Tabs } from "../../atoms";
import { CursorClick02Icon } from "../../atoms/icons/CursorClick02Icon";
import { ShopSidebarContext } from "../../Shop/ShopSidebar/ShopSidebarContext";
import { CreditPackItem } from "./components/CreditPackItem/CreditPackItem";
import { InfiniteScrollContainer } from "./components/InfiniteScrollContainer";
import { UsageItem } from "./components/UsageItem/UsageItem";
import { TABS_USER_SESSIONS } from "./constants";
import { useIsLargeScreen } from "./hooks/useIsLargeScreen";
import { TabsType, UserSessionsProps } from "./types";

export const UserSessions = ({ user, isFetchingUser }: UserSessionsProps) => {
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>();
  const [activeTabId, _setActiveTabId] = useState<TabsType>(
    TABS_USER_SESSIONS[0].id,
  );

  const isLargeScreen = useIsLargeScreen();
  const { t } = useTranslation();
  const { showShop } = useContext(ShopSidebarContext);
  const { api } = useGymflowModels();

  const queryUsageHistory =
    useInfiniteQueryUserMemberSessionPackSubscriptionUsageHistory({
      api,
      subscriptionId: selectedItemId || 0,
      enabled: !!selectedItemId,
    });

  const { data: dataUsageHistory, isFetching: isFetchingUsageHistory } =
    queryUsageHistory;
  const listOfUsage = dataUsageHistory?.pages.flatMap((e) => e.content) || [];

  const userMemberId = user?.id ?? "";

  const queryUserMemberSessionPackSubscriptionCurrent =
    useInfiniteQueryUserMemberSessionPackSubscriptionCurrent({
      api,
      userMemberId,
      enabled: activeTabId === "CURRENT" && !isFetchingUser,
    });
  const queryUserMemberSessionPackSubscriptionPrevious =
    useInfiniteQueryUserMemberSessionPackSubscriptionPrevious({
      api,
      userMemberId,
      enabled: activeTabId === "PREVIOUS" && !isFetchingUser,
    });

  const queryCreditPacks =
    activeTabId === "CURRENT"
      ? queryUserMemberSessionPackSubscriptionCurrent
      : queryUserMemberSessionPackSubscriptionPrevious;
  const { data: dataCreditPacks, isFetching: isFetchingCreditPacks } =
    queryCreditPacks;
  const listOfCreditPacks =
    dataCreditPacks?.pages.flatMap((e) => e.content) || [];

  const info = useMemo(() => {
    if (!listOfCreditPacks?.length || (selectedItemId && !listOfUsage.length)) {
      return {
        icon: <SearchIcon className="h-6 w-6" />,
        title: t(
          "page.userMember.tab.userSessions.usageList.info.noUsageYet.title",
        ),
        description: null,
      };
    }

    if (!selectedItemId) {
      return {
        icon: <CursorClick02Icon className="h-6 w-6" />,
        title: t(
          "page.userMember.tab.userSessions.usageList.info.selectPackage.title",
        ),
        description: t(
          "page.userMember.tab.userSessions.usageList.info.selectPackage.description",
        ),
      };
    }
    return { icon: null, title: null, description: null };
  }, [listOfCreditPacks?.length, selectedItemId, listOfUsage.length, t]);

  const setActiveTabId = (id: TabsType) => {
    _setActiveTabId(id);
    setSelectedItemId(undefined);
  };

  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0 flex flex-col gap-3 lg:flex-row">
        <div className="bg-gray-0 flex min-h-0 flex-col rounded-2xl border border-gray-200 lg:flex-1">
          <Tabs
            tabs={TABS_USER_SESSIONS}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
            className="justify-evenly p-4"
            tabClassName="py-2 px-3 max-w-none w-full items-center justify-center"
            activeTabClassName="bg-secondary-25 rounded-lg border-transparent"
          />
          <InfiniteScrollContainer
            className="flex flex-1 flex-row gap-3 overflow-x-auto p-4 lg:!flex-col lg:overflow-y-auto"
            direction={isLargeScreen ? "vertical" : "horizontal"}
            query={queryCreditPacks}
          >
            {listOfCreditPacks?.length > 0 ? (
              listOfCreditPacks.map((item) => (
                <CreditPackItem
                  key={item.id}
                  creditPack={item}
                  userMemberId={userMemberId}
                  activeTabId={activeTabId}
                  setSelectedItemId={setSelectedItemId}
                  selectedItemId={selectedItemId}
                />
              ))
            ) : isFetchingCreditPacks ? (
              <Spinner className="h-7 w-7" />
            ) : (
              <div className="flex w-full flex-col items-center justify-center gap-4 overflow-y-auto p-1 lg:pt-10">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-8 border-gray-100 bg-gray-200">
                  <SearchIcon className="h-6 w-6" />
                </div>
                <div className="flex flex-col items-center justify-center gap-6">
                  <div className="flex flex-col items-center justify-center gap-1">
                    <div className="text-base font-semibold text-gray-950">
                      {t(
                        "page.userMember.tab.userSessions.creditPacksList.info.noPackages.title",
                      )}
                    </div>
                    <div className="px-10 text-center text-sm font-normal text-gray-500">
                      {t(
                        "page.userMember.tab.userSessions.creditPacksList.info.noPackages.description",
                      )}
                    </div>
                  </div>
                  <Button
                    intent="secondary"
                    className="gap-1.5"
                    onClick={() => showShop(user)}
                  >
                    <ShopBagIcon
                      className="h-5 w-5"
                      pathClassName="stroke-gray-0"
                    />
                    {t(
                      "page.userMember.tab.userSessions.creditPacksList.info.noPackages.button",
                    )}
                  </Button>
                </div>
              </div>
            )}
          </InfiniteScrollContainer>
        </div>
        <div className="bg-gray-0 flex min-h-0 flex-1 flex-col gap-4 rounded-2xl border border-gray-200 pt-4">
          <div className="ml-4 text-lg font-semibold text-gray-950">
            {t("page.userMember.tab.userSessions.usageList.title")}
          </div>
          {selectedItemId && listOfUsage?.length > 0 ? (
            <InfiniteScrollContainer
              className="flex flex-1 flex-col overflow-y-auto px-4 pb-4"
              direction="vertical"
              query={queryUsageHistory}
            >
              {listOfUsage.map((item, index) => (
                <UsageItem
                  key={`${item.name}-${index}`}
                  usageInfo={item}
                  last={listOfUsage.length - 1 > index}
                />
              ))}
            </InfiniteScrollContainer>
          ) : isFetchingUsageHistory ? (
            <Spinner className="h-7 w-7" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 overflow-y-auto pt-10">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-8 border-gray-100 bg-gray-200">
                {info.icon}
              </div>
              <div className="flex flex-col items-center justify-center gap-1 ">
                <div className="text-base font-semibold text-gray-950">
                  {info.title}
                </div>
                <div className="px-10 text-center text-sm font-normal text-gray-500">
                  {info.description}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
